import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserX } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';
import LeadStatusPicker from './LeadStatusPicker';

// Hot keys from plan 5.4 - labelling a reply is the action people repeat
// hundreds of times a day, so it must not need the mouse.
const HOT_KEYS = { i: 'interested', n: 'not_interested', f: 'follow_up' };

// Everything that ties this conversation to a message we sent: a reply from a
// role mailbox carries another address than the one the campaign wrote to.
const threadReferences = (messages) => [
  ...new Set(
    messages
      .flatMap((message) => [
        ...message.references,
        message.in_reply_to,
        message.is_outbound ? message.message_id : null,
      ])
      .filter(Boolean)
  ),
];

export default function UniboxLeadStatus({ address, messages, statuses }) {
  const { t } = useTranslation('common');
  const [lead, setLead] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLead(await outreachApi.resolveLead({ address, references: threadReferences(messages) }));
      setMissing(false);
    } catch (e) {
      if (e?.response?.status !== 404) throw e;
      setLead(null);
      setMissing(true);
    }
  }, [address, messages]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (payload) => {
    await outreachApi.setLeadStatus(lead.id, payload);
    await load();
  };

  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.metaKey || event.ctrlKey) return;
      const key = HOT_KEYS[event.key.toLowerCase()];
      if (!key || !lead) return;
      const status = statuses.find((s) => s.key === key);
      // A status needing a date can't be a one-key action, so the hot key
      // deliberately does nothing rather than inventing a date.
      if (!status || status.requires_date) return;
      event.preventDefault();
      apply({ pipelineStatus: key, followUpAt: null });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (missing)
    return (
      <span className="unibox-icon-note" title={t('outreach.leads.notALead')}>
        <UserX size={14} />
      </span>
    );
  if (!lead) return null;

  return (
    <div className="unibox-detail-meta">
      <span>{t('outreach.leads.pipelineStatus')}</span>
      <LeadStatusPicker compact statuses={statuses} value={lead.pipeline_status} onApply={apply} />
      <span className="unibox-badge">
        {t(`outreach.leads.engagement.${lead.engagement_status}`, lead.engagement_status)}
      </span>
      {/* They answered from another address than the one we wrote to. */}
      {lead.email.toLowerCase() !== address.toLowerCase() && (
        <span className="lead-history-source">{lead.email}</span>
      )}
      <span className="lead-history-source">{t('outreach.leads.hotKeys')}</span>
    </div>
  );
}
